import { useEffect, useState } from 'react';

import { CSSProperties } from 'styled-components';
import { Collapse as ChakraCollapse } from '@chakra-ui/react';

import { withIsMounted } from '~/shared/utils/withIsMounted';

import { WithChildren } from '~/shared/types/WithChildren';

interface ICollapseProps {
  isOpen?: boolean;
  contentStyle?: CSSProperties;
  startingHeight?: number;
}

export const Collapse: WithChildren<ICollapseProps> = withIsMounted(
  ({ isOpen = false, children, contentStyle, startingHeight }) => {
    // Chakra keeps the children mounted at height 0, so without this the
    // collapsed content would stay in the tab order and exposed to screen
    // readers while invisible. `visibility: hidden` removes it from both.
    const canBeInert = !isOpen && !startingHeight;

    // `visibility: hidden` cannot be applied while the content is still
    // collapsing, or the close animation is replaced by an instant disappear.
    // It is armed only once the transition settles; opening clears it up front
    // so the content is visible for the whole expand.
    const [isInert, setIsInert] = useState(canBeInert);

    useEffect(() => {
      if (!canBeInert) setIsInert(false);
    }, [canBeInert]);

    return (
      <ChakraCollapse
        in={isOpen}
        animateOpacity
        style={{
          ...contentStyle,
          // Set explicitly in both directions so reopening always restores it.
          visibility: isInert ? 'hidden' : 'visible',
        }}
        startingHeight={startingHeight}
        onAnimationComplete={() => setIsInert(canBeInert)}
        aria-hidden={canBeInert || undefined}
      >
        {children}
      </ChakraCollapse>
    );
  }
);
